/**
 * Per-route document metadata.
 *
 * Read by two consumers that must never disagree: `useSeo` at runtime, and the
 * prerender step that writes static HTML for crawlers which do not execute
 * JavaScript. Keeping one table means a crawler and a human are shown the same
 * title and description for the same URL.
 *
 * Descriptions are written to be true of a coaching business whose specifics are
 * still placeholders - they describe the *service*, which is known, rather than
 * claiming credentials, outcomes or locations, which are not. A description
 * containing `[COACH NAME]` would be worse than a slightly generic one, because
 * it would ship to a search result page.
 */

import { brand, pathways } from './brand.js';
import { publicRoutes } from './routes.js';
import { socialAssets } from './assets.js';

export interface PageSeo {
  /** Without the business name; `useSeo` appends it. */
  title: string;
  /** Aim for 110-155 characters so it is not truncated in a search result. */
  description: string;
  /** Open Graph image, app-relative. */
  imageUrl: string;
  /** Authenticated and transient routes are excluded from indexing. */
  noIndex?: boolean;
}

const MASTER_IMAGE = socialAssets.masterOpenGraph;
const PT_IMAGE = socialAssets.personalTrainingOpenGraph;
const BOXING_IMAGE = socialAssets.boxingOpenGraph;

/** Keyed by `RouteDefinition.id`. */
export const pageSeo: Record<string, PageSeo> = {
  [publicRoutes.home.id]: {
    title: pathways.master.tagline,
    description:
      'Personal training, nutrition, conditioning and boxing coaching designed around the individual. One coach, one standard, two distinct ways to train.',
    imageUrl: MASTER_IMAGE,
  },

  [publicRoutes.personalTraining.id]: {
    title: `${pathways['personal-training'].label} - Personal Training & Nutrition`,
    description:
      'Structured personal training that combines resistance work, nutrition and cardiovascular conditioning in one plan, built around your goals, experience and schedule.',
    imageUrl: PT_IMAGE,
  },

  [publicRoutes.boxing.id]: {
    title: `${pathways.boxing.label} - Boxing Coaching`,
    description:
      'One-to-one boxing coaching for technique, movement, conditioning and confidence. Suitable for complete beginners through to competition preparation.',
    imageUrl: BOXING_IMAGE,
  },

  [publicRoutes.programs.id]: {
    title: 'Coaching Programs',
    description:
      'Compare every coaching program: one-to-one personal training, strength and muscle development, fat loss, nutrition coaching, conditioning, boxing and online coaching.',
    imageUrl: MASTER_IMAGE,
  },

  [publicRoutes.results.id]: {
    title: 'Client Results',
    description:
      'Progress records from real coaching clients, filterable by pathway and goal. Every record is published with the client’s written consent.',
    imageUrl: MASTER_IMAGE,
  },

  [publicRoutes.about.id]: {
    title: 'About the Coach',
    description:
      'Coaching philosophy, qualifications, experience and training locations, and how assessment, planning, coaching, measurement and progression fit together.',
    imageUrl: MASTER_IMAGE,
  },

  [publicRoutes.coachCard.id]: {
    title: 'Zach Jones - Personal Trainer & Combat Coach',
    description:
      'Connect with Zach Jones, personal trainer and combat coach at Iron Resolve Combat Fitness. Save his details, book a consultation or follow his journey.',
    imageUrl: socialAssets.masterOpenGraph,
  },

  [publicRoutes.book.id]: {
    title: 'Book a Consultation',
    description:
      'Start with a consultation to work out which coaching pathway suits your goals, your experience and the way you prefer to train.',
    imageUrl: MASTER_IMAGE,
  },

  [publicRoutes.contact.id]: {
    title: 'Contact',
    description:
      'Training locations, operating hours and an enquiry form for coaching questions, availability and consultation bookings.',
    imageUrl: MASTER_IMAGE,
  },

  // Legal pages are indexable: a prospective client should be able to read the
  // cancellation policy before booking, and search engines are how they find it.
  [publicRoutes.privacy.id]: {
    title: 'Privacy Policy',
    description:
      'How coaching records, health screening answers, progress photographs and messages are collected, stored, used and deleted.',
    imageUrl: MASTER_IMAGE,
  },
  [publicRoutes.terms.id]: {
    title: 'Terms & Conditions',
    description: 'The terms that apply to coaching services, bookings, packages and session credits.',
    imageUrl: MASTER_IMAGE,
  },
  [publicRoutes.trainingDisclaimer.id]: {
    title: 'Training Disclaimer',
    description:
      'The scope of coaching services, the limits of exercise advice, and when medical clearance is required before training.',
    imageUrl: MASTER_IMAGE,
  },
  [publicRoutes.nutritionDisclaimer.id]: {
    title: 'Nutrition Disclaimer',
    description:
      'The scope of nutrition guidance provided, and the difference between coaching education and clinical dietetic advice.',
    imageUrl: MASTER_IMAGE,
  },
  [publicRoutes.cancellationPolicy.id]: {
    title: 'Cancellation Policy',
    description:
      'Notice periods for cancelling or rescheduling a session, how session credits are treated, and how to request a change.',
    imageUrl: MASTER_IMAGE,
  },
  [publicRoutes.accessibility.id]: {
    title: 'Accessibility',
    description:
      'The accessibility standard this site targets, the features provided, known limitations, and how to report a barrier.',
    imageUrl: MASTER_IMAGE,
  },

  // Authentication routes: real pages, deliberately not indexed. A sign-in form
  // in a search result is noise, and a password-reset URL there is a hazard.
  [publicRoutes.login.id]: {
    title: 'Client Login',
    description: 'Sign in to the coaching portal.',
    imageUrl: MASTER_IMAGE,
    noIndex: true,
  },
  [publicRoutes.register.id]: {
    title: 'Create Your Account',
    description: 'Create a coaching account to complete onboarding and book your first session.',
    imageUrl: MASTER_IMAGE,
    noIndex: true,
  },
  [publicRoutes.forgotPassword.id]: {
    title: 'Forgot Password',
    description: 'Request a password reset link.',
    imageUrl: MASTER_IMAGE,
    noIndex: true,
  },
  [publicRoutes.resetPassword.id]: {
    title: 'Reset Password',
    description: 'Choose a new password.',
    imageUrl: MASTER_IMAGE,
    noIndex: true,
  },
  [publicRoutes.verifyEmail.id]: {
    title: 'Verify Your Email',
    description: 'Confirm your email address to activate your coaching account.',
    imageUrl: MASTER_IMAGE,
    noIndex: true,
  },
};

/**
 * Metadata for a route, falling back to the master description.
 *
 * The fallback exists so a newly added route degrades to accurate-but-generic
 * metadata instead of inheriting whichever page the visitor came from.
 */
export function seoForRoute(routeId: string): PageSeo {
  return (
    pageSeo[routeId] ?? {
      title: brand.businessName,
      description: pathways.master.supporting,
      imageUrl: MASTER_IMAGE,
      noIndex: true,
    }
  );
}
